<template>
  <page-template :is-loading="isLoading" :data-exists="selectedSecurityAlertData !== null">
    <template #pageHeadline>
      {{
        isNew
          ? $t('securityAlert.configPage.newPageHeadline')
          : $t('securityAlert.configPage.editPageHeadline')
      }}
    </template>
    <template #secondLevelHeaderLeft>
      <rbz-form-button
        variant="tertiary"
        icon="IconArrowBack"
        v-b-tooltip="$t('common.operations.returnToList')"
        link-route-name="Security Alerts List"
        classes="redirect-to-list-button"
        data-qa="redirect-to-list-button"
      />
      <template v-if="selectedSecurityAlert && !isNew">
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
        <rbz-dropdown
          :items="dropdownData.map((item) => [item.id, item.name])"
          :disabled="isLoading"
          v-model:value="selectedSecurityAlert"
          @update:value="switchSecurityAlert($event)"
          :ignore-update-in-process="true"
          type="single-select"
          :header-tooltip="$t('securityAlert.configPage.switchTooltip')"
          classes="switch-security-alert switch-page-entry"
          data-qa="switch-security-alert"
        />
      </template>
    </template>
    <template #secondLevelHeaderRight>
      <template v-if="selectedSecurityAlert && !isNew">
        <rbz-form-button
          :label="$t('common.operations.duplicate.label')"
          variant="tertiary"
          icon="IconDuplicate"
          @button-click="duplicateSecurityAlert()"
          class="duplicate-button"
          :disabled="isLoading || entityNotSaved"
          :tooltip="
            entityNotSaved ? $t('common.operations.duplicate.disabledTooltip.notSaved') : ''
          "
          data-qa="duplicate-button"
        />
        <rbz-form-button
          :label="$t('common.operations.delete.label')"
          variant="tertiary"
          icon="IconDelete"
          @button-click="openDeleteSecurityAlertModal()"
          class="delete-button"
          :disabled="isLoading"
          data-qa="delete-button"
        />
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
      </template>
      <rbz-form-button
        :label="$t('common.operations.save.label')"
        :disabled="isLoading || !isSecurityAlertValid"
        @button-click="saveChanges()"
        class="save-changes-button"
        data-qa="save-changes-button"
      />
    </template>
    <template #pageContent>
      <div class="rbz-columns">
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-form-input
              name="name"
              classes="name"
              data-qa="name-input"
              :label="$t('securityAlert.configPage.name')"
              :placeholder="$t('securityAlert.configPage.name')"
              v-model:inputValue="selectedSecurityAlertData.name"
            >
              <template #postLabel>
                <span
                  v-b-tooltip="$t('securityAlert.configPage.nameAdditionalInfoTooltip')"
                  class="info-icon-wrapper"
                >
                  <icon-info-small color="dusty-gray" class="info-icon" />
                </span>
              </template>
            </rbz-form-input>
          </div>
        </div>
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-dropdown
              type="multi-select"
              :items="serverGroupItems"
              v-model:value="checkedServerGroupIDs"
              :tag-click-enabled="false"
              :label="$t('securityAlert.configPage.serverGroups')"
              :placeholder="$t('securityAlert.configPage.serverGroups')"
              :all-label="$t('securityAlert.configPage.selectAll')"
              :exclusive-option-ids="exclusiveOptionIds"
              class="server-groups-selection"
              data-qa="server-groups-selection"
            >
              <template #postLabel>
                <span
                  v-b-tooltip="$t('securityAlert.configPage.serverGroupsAdditionalInfoTooltip')"
                  class="info-icon-wrapper"
                >
                  <icon-info-small color="dusty-gray" class="info-icon" />
                </span>
              </template>
            </rbz-dropdown>
          </div>
        </div>
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-form-input
              name="recipients"
              class="recipients"
              data-qa="recipients-input"
              :label="$t('securityAlert.configPage.recipients')"
              :placeholder="$t('securityAlert.configPage.recipients')"
              :inputValue="selectedSecurityAlertData.recipients.join(', ')"
              @update:inputValue="recipientsUpdated($event)"
            >
              <template #postLabel>
                <span
                  v-b-tooltip="$t('securityAlert.configPage.recipientsAdditionalInfoTooltip')"
                  class="info-icon-wrapper"
                >
                  <icon-info-small color="dusty-gray" class="info-icon" />
                </span>
              </template>
            </rbz-form-input>
          </div>
        </div>
      </div>
      <div class="rbz-columns">
        <div class="rbz-column">
          <div class="rbz-field">
            <rbz-table
              :columns="dynamicRulesTableColumns"
              :table-data="dynamicRulesTableData"
              class="dynamic-rules-table"
              data-qa="dynamic-rules-table"
              ref="dynamicRulesTable"
              rows-per-page="20"
              :row-buttons="dynamicRulesTableRowButtons"
              :show-download-button="false"
              :show-filter-button="false"
              :show-new-as-row="true"
              :show-new-row-at-bottom="true"
              :new-button-label="$t('securityAlert.configPage.dynamicRulesTable.addNew')"
              :sticky-header="false"
              @add-inline-data="addDynamicRules($event.dynamicRules)"
              @delete-row-button-clicked="removeDynamicRule($event.id)"
            >
              <template #tableResultsOverride>
                <router-link
                  :to="dynamicRulesListPath"
                  @click="null"
                  class="router-link"
                  data-qa="dynamic-rules-link"
                  target="_blank"
                >
                  <div class="dynamic-rules-link">
                    <span class="dynamic-rules-title ellipsis">
                      {{ $t('securityAlert.configPage.dynamicRules') }}
                    </span>
                    <span class="link-icon">
                      <icon-open-new color="fun-blue" />
                    </span>
                  </div>
                </router-link>
              </template>
              <template #newRowOverride="newRowProps">
                <template v-if="newDynamicRules.length === 0">
                  <td class="all-connected-data-cell" colspan="8">
                    <div class="data-cell-content ellipsis has-text-centered">
                      <span>
                        {{ $t('securityAlert.configPage.dynamicRulesTable.allConnected') }}
                      </span>
                    </div>
                  </td>
                  <td>
                    <form-confirmation :show-confirm="false" @cancel="cancelAddDynamicRules()" />
                  </td>
                </template>
                <template v-else>
                  <td class="new-data-cell" colspan="2">
                    <rbz-dropdown
                      type="multi-select"
                      classes="dynamic-rules-selection"
                      :items="
                        newDynamicRules.map((item) => {
                          return [item.id, item.name];
                        })
                      "
                      data-qa="dynamic-rules-dropdown"
                      v-model:value="newRowProps.data.dynamicRules"
                    />
                  </td>
                  <td colspan="6"></td>
                </template>
              </template>
              <template #newRowOverrideConfirmationButtons v-if="newDynamicRules.length === 0">
                <template></template>
              </template>
            </rbz-table>
          </div>
        </div>
      </div>
      <rbz-modal-dialog
        :show="showDeleteSecurityAlertModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteSecurityAlert"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteSecurityAlertModal = false"
        @closeModal="showDeleteSecurityAlertModal = false"
        size="medium"
      >
        <template #content>
          {{
            $t('common.operations.delete.modal.message', { name: selectedSecurityAlertData.name })
          }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>

<script lang="ts">
import _ from 'lodash';
import { defineComponent } from 'vue';
import {
  Action,
  BasicDocument,
  DocumentName,
  DynamicRule,
  SecurityAlert,
  ServerGroup,
} from '@/types';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import RbzFormButton from '@/components/RbzFormButton.vue';
import RbzDropdown from '@/components/RbzDropdown.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import PageTemplate from '@/views/PageTemplate.vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { usePlanetStore } from '@/store/PlanetStore';
import { TARGET_OPTIONS } from '@/helpers/Consts';
import { useRouting } from '@/helpers/RoutingUtils';
import IconOpenNew from 'ui-component-library/assets/icons/IconOpenNew.vue';
import IconInfoSmall from 'ui-component-library/assets/icons/IconInfoSmall.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import FormConfirmation from 'ui-component-library/rbz/components/FormConfirmation.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';

export default defineComponent({
  name: 'SecurityAlertsEditor',
  setup() {
    const { getRouteName, getListPath, navigateTo } = useRouting();
    return { getRouteName, getListPath, navigateTo };
  },
  components: {
    FormConfirmation,
    RbzFormInput,
    IconInfoSmall,
    IconOpenNew,
    LayoutDividerLine,
    PageTemplate,
    RbzDropdown,
    RbzFormButton,
    RbzModalDialog,
    RbzTable,
  },
  data() {
    return {
      dropdownData: [] as BasicDocument[],
      isNew: false,
      selectedSecurityAlert: null as string,
      selectedSecurityAlertData: null as SecurityAlert,
      originalSecurityAlertData: null as SecurityAlert,
      pageLoadingCounter: null,
      isDeleteLoading: false,
      showDeleteSecurityAlertModal: false,

      exclusiveOptionIds: ['__all__'],

      serverGroups: [] as ServerGroup[],
      dynamicRules: [] as DynamicRule[],
      actions: [] as Action[],

      dynamicRulesTargetOptions: _.map(TARGET_OPTIONS, (targetOption) => {
        return {
          key: targetOption.key,
          title: this.$t(targetOption.translationKey),
        };
      }),
      dynamicRulesTableColumns: [
        {
          id: 'name',
          title: this.$t('securityAlert.configPage.dynamicRulesTable.name'),
          fieldNames: ['name'],
          isSortable: false,
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'description',
          title: this.$t('securityAlert.configPage.dynamicRulesTable.description'),
          fieldNames: ['description'],
          isSortable: false,
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'target',
          title: this.$t('securityAlert.configPage.dynamicRulesTable.target'),
          fieldNames: ['target'],
          displayFunction: (dynamicRule: DynamicRule) => {
            const target = dynamicRule.target.split('_');
            const targetType = target.shift();
            const targetOption = _.find(this.dynamicRulesTargetOptions, (targetOption) => {
              return targetOption.key === targetType;
            });
            return targetOption?.title || '';
          },
          isSortable: false,
          classes: 'width-100px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'threshold',
          title: this.$t('securityAlert.configPage.dynamicRulesTable.threshold'),
          fieldNames: ['threshold'],
          isSortable: false,
          classes: 'width-140px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'timeframe',
          title: this.$t('securityAlert.configPage.dynamicRulesTable.timeFrame'),
          fieldNames: ['timeframe'],
          isSortable: false,
          classes: 'width-140px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'action',
          title: this.$t('securityAlert.configPage.dynamicRulesTable.action'),
          fieldNames: ['action'],
          displayFunction: (item: DynamicRule) => {
            const action = _.find(this.actions, (action) => {
              return action.id === item.action;
            });
            return action?.name || '';
          },
          isSortable: false,
          classes: 'width-140px action-cell',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'quarantine-time',
          title: this.$t('securityAlert.configPage.dynamicRulesTable.quarantineTime'),
          fieldNames: ['ttl'],
          displayFunction: (item: DynamicRule) => {
            return item.ttl / 3600;
          },
          isSortable: false,
          classes: 'width-180px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'tags',
          title: this.$t('securityAlert.configPage.dynamicRulesTable.tags'),
          fieldNames: ['tags'],
          displayFunction: (item: DynamicRule) => {
            return item.tags?.join(', ') || '';
          },
          isSortable: false,
          classes: 'width-250px',
          cellContentClasses: 'ellipsis',
        },
      ] as RbzTableColumnOptions[],
      dynamicRulesTableRowButtons: [
        {
          id: 'delete',
          tooltip: this.$t('common.operations.delete.tableTooltip'),
          icon: 'IconDelete',
        },
      ] as RbzTableRowButton[],
      addAllDynamicRulesOptionId: DatasetsUtils.generateUUID2(),
    };
  },
  watch: {
    $route: {
      handler: async function (val) {
        if (['New Security Alert', 'Security Alerts Editor'].includes(val.name)) {
          this.setPageLoadingStatus(true);
          this.isNew = val.name.startsWith('New');
          await this.branchesStore.getBranches();
          await this.loadDropdownData();
          if (val.name === 'New Security Alert') {
            this.selectedSecurityAlertData = this.newSecurityAlert();
            this.selectedSecurityAlertData.name = Utils.generateUniqueEntityName(
              this.selectedSecurityAlertData.name,
              _.map(this.dropdownData, 'name')
            );
            this.originalSecurityAlertData = _.cloneDeep(this.selectedSecurityAlertData);
          }
          if (val.name === 'Security Alerts Editor') {
            const securityAlertId = this.$route.params?.doc_id?.toString();
            const dataExists = this.dropdownData.some((item) => {
              return item.id === securityAlertId;
            });
            if (dataExists) {
              await this.loadSecurityAlert(securityAlertId);
            } else {
              this.redirectToList();
              return;
            }
          }
          const savedSessionData = sessionStorage.getItem('formData');
          if (savedSessionData) {
            this.selectedSecurityAlertData = JSON.parse(savedSessionData);
          }
          const serverGroupsPromise = this.loadServerGroups();
          const dynamicRulesPromise = this.loadDynamicRules();
          const actionsPromise = this.loadActions();
          await Promise.all([serverGroupsPromise, dynamicRulesPromise, actionsPromise]);
          if (this.isNew) {
            this.checkedServerGroupIDs = this.selectedSecurityAlertData['server_groups'];
          }
          this.setSessionDataWatcher();
          this.setPageLoadingStatus(false);
        }
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, usePlanetStore, useUserStore),

    entityName(): DocumentName {
      return {
        plural: this.$t('securityAlert.plural'),
        singular: this.$t('securityAlert.singular'),
      };
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    isLoading(): boolean {
      return (
        this.pageLoadingCounter === null || this.pageLoadingCounter > 0 || this.isDeleteLoading
      );
    },

    entityNotSaved(): boolean {
      return !_.isEqual(this.selectedSecurityAlertData, this.originalSecurityAlertData);
    },

    isSecurityAlertValid(): boolean {
      const trimmedName = this.selectedSecurityAlertData.name.toLowerCase().trim();
      const nameExists = this.dropdownData.filter((item) => {
        return item.name.toLowerCase().trim() === trimmedName;
      }).length;
      const nameValid =
        this.selectedSecurityAlertData.name &&
        (trimmedName === this.originalSecurityAlertData.name.toLowerCase().trim() || !nameExists);
      const recipientsNotEmpty = this.selectedSecurityAlertData['recipients'].length > 0;
      return nameValid && recipientsNotEmpty;
    },

    dynamicRulesTableData(): DynamicRule[] {
      return _.map(
        this.selectedSecurityAlertData['dynamic_rules'],
        (dynamicRuleId: DynamicRule['id']) => {
          return this.dynamicRules.find((dynamicRule: DynamicRule) => {
            return dynamicRule.id === dynamicRuleId;
          });
        }
      ).filter((dynamicRule: DynamicRule) => !!dynamicRule);
    },

    newDynamicRules(): BasicDocument[] {
      return _.filter(this.dynamicRules, (dynamicRule) => {
        return _.indexOf(this.selectedSecurityAlertData['dynamic_rules'], dynamicRule.id) === -1;
      });
    },

    dynamicRulesListPath(): string {
      return `/${this.selectedBranch}/dynamic-rules/list`;
    },

    serverGroupItems() {
      // add custom item for selecting all groups
      return [
        ['__all__', this.$t('securityAlert.configPage.allServerGroups')],
        ...this.serverGroups.map((item) => [item.id, item.name]),
      ];
    },

    checkedServerGroupIDs: {
      get() {
        if (this.selectedSecurityAlertData['server_groups'].includes('__all__')) {
          return ['__all__'];
        } else {
          return this.selectedSecurityAlertData['server_groups'];
        }
      },
      set(values) {
        if (values.includes('__all__')) {
          this.selectedSecurityAlertData['server_groups'] = [
            ...this.serverGroupItems.map((item) => item[0]),
          ];
        } else {
          this.selectedSecurityAlertData['server_groups'] = values;
        }
      },
    },
  },
  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    redirectToList() {
      this.navigateTo(this.getListPath('security-alerts'));
    },

    switchSecurityAlert(securityAlertId: string) {
      this.$router.push(`/${this.selectedBranch}/security-alerts/config/${securityAlertId}`);
    },

    async loadDropdownData() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/security-alerts`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.dropdownData = Utils.sortArrayByName(response?.data?.items) as BasicDocument[];
      this.setPageLoadingStatus(false);
    },

    newSecurityAlert(): SecurityAlert {
      const factory = DatasetsUtils.newEntityFactory['security-alerts'];
      return factory && factory();
    },

    async loadSecurityAlert(securityAlertId: string) {
      this.setPageLoadingStatus(true);
      this.selectedSecurityAlert = securityAlertId;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/security-alerts/${securityAlertId}`,
      });
      this.selectedSecurityAlertData = response?.data || {};
      this.originalSecurityAlertData = _.cloneDeep(this.selectedSecurityAlertData);
      this.setPageLoadingStatus(false);
    },

    async duplicateSecurityAlert() {
      this.setPageLoadingStatus(true);
      const securityAlertToAdd = _.cloneDeep(this.selectedSecurityAlertData);
      securityAlertToAdd.id = DatasetsUtils.generateUUID2();
      securityAlertToAdd.name = Utils.generateUniqueEntityName(
        securityAlertToAdd.name,
        _.map(this.dropdownData, 'name'),
        true
      );
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      const url = `conf/${this.selectedBranch}/security-alerts/${securityAlertToAdd.id}`;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url,
        data: securityAlertToAdd,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.switchSecurityAlert(securityAlertToAdd.id);
      }
      this.setPageLoadingStatus(false);
    },

    openDeleteSecurityAlertModal() {
      this.showDeleteSecurityAlertModal = true;
    },

    async deleteSecurityAlert() {
      this.isDeleteLoading = true;
      const securityAlertId = this.selectedSecurityAlert;
      const dropdownIndex = _.findIndex(this.dropdownData, (item) => {
        return item.id === securityAlertId;
      });
      this.dropdownData.splice(dropdownIndex, 1);
      this.selectedSecurityAlert = null;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/security-alerts/${securityAlertId}`,
        successMessage,
        failureMessage,
      });
      this.redirectToList();
      this.isDeleteLoading = false;
    },

    async saveChanges() {
      this.setPageLoadingStatus(true);
      let successMessage;
      let failureMessage;
      if (this.isNew) {
        successMessage = this.$t('common.operations.new.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.new.messages.failure', this.entityName);
      } else {
        successMessage = this.$t('common.operations.save.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.save.messages.failure', this.entityName);
      }
      this.selectedSecurityAlertData.name = this.selectedSecurityAlertData.name.trim();
      this.selectedSecurityAlertData.recipients = _.filter(
        this.selectedSecurityAlertData.recipients,
        (item: string) => {
          return !!item;
        }
      );

      // assume all server groups selected if none were selected by the user
      if (!this.selectedSecurityAlertData['server_groups'].length) {
        this.checkedServerGroupIDs = ['__all__'];
        await this.loadServerGroups();
      }

      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: this.isNew ? 'POST' : 'PUT',
        url: `conf/${this.selectedBranch}/security-alerts/${this.selectedSecurityAlertData.id}`,
        data: this.selectedSecurityAlertData,
        successMessage,
        failureMessage,
        displayServerFailureMessage: true,
      });
      if (response) {
        if (this.isNew) {
          const currentRoute = this.$route.path;
          const targetRoute = currentRoute.replace(
            'new',
            `config/${this.selectedSecurityAlertData.id}`
          );
          this.$router.push(targetRoute);
        } else {
          await this.loadDropdownData();
          this.originalSecurityAlertData = _.cloneDeep(this.selectedSecurityAlertData);
        }
      }
      this.setPageLoadingStatus(false);
    },

    async loadServerGroups() {
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/server-groups`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.serverGroups = Utils.sortArrayByName(response?.data?.items) as ServerGroup[];
    },

    async loadDynamicRules() {
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/dynamic-rules`,
      });
      this.dynamicRules = Utils.sortArrayByName(response?.data?.items) as DynamicRule[];
    },

    async loadActions() {
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/actions`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.actions = Utils.sortArrayByName(response?.data?.items) as Action[];
    },

    recipientsUpdated(newValue: string) {
      const recipients = newValue.split(',');
      this.selectedSecurityAlertData.recipients = _.map(recipients, (item) => {
        return item.trim();
      });
    },

    addDynamicRules(dynamicRuleIds: DynamicRule['id'][]) {
      _.forEach(dynamicRuleIds, (dynamicRuleId: DynamicRule['id']) => {
        if (dynamicRuleId) {
          this.selectedSecurityAlertData['dynamic_rules'].push(dynamicRuleId);
        }
      });
    },

    cancelAddDynamicRules() {
      this.$refs.dynamicRulesTable.setShowNewRow(false);
    },

    removeDynamicRule(dynamicRuleId: DynamicRule['id']) {
      const index = _.findIndex(this.selectedSecurityAlertData['dynamic_rules'], (item) => {
        return item === dynamicRuleId;
      });
      this.selectedSecurityAlertData['dynamic_rules'].splice(index, 1);
    },

    setSessionDataWatcher() {
      this.$watch(
        () => [this.selectedSecurityAlertData, this.entityNotSaved],
        (params) => {
          const [formData, entityNotSaved] = params;
          Utils.saveSessionData(formData, entityNotSaved);
        },
        {
          deep: true,
          immediate: true,
        }
      );
    },
  },
  beforeRouteLeave() {
    Utils.clearSessionData();
  },
});
</script>
<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

.dynamic-rules-link {
  align-items: center;
  color: map.get(colors.$colors, 'fun-blue');
  display: flex;
  gap: 8px;

  :deep(.link-icon) {
    height: 16px;
    width: 16px;

    svg {
      height: 16px;
      width: 16px;
    }
  }
}

.all-connected-data-cell,
.new-data-cell {
  color: map.get(colors.$colors, 'shuttle-gray');
  vertical-align: middle;
}
</style>
